import * as i0 from '@angular/core';
import { inject, EventEmitter, Output, Input, HostBinding, Component, InjectionToken, Injector, Injectable, ChangeDetectionStrategy, NgModule } from '@angular/core';
import * as i1 from '@angular/common';
import { CommonModule } from '@angular/common';
import * as i3 from '@tc/interstellar-button';
import { StlrButtonModule } from '@tc/interstellar-button';
import * as i2 from '@tc/interstellar-icon';
import { IconTranslationService, StlrIconModule } from '@tc/interstellar-icon';
import { Overlay } from '@angular/cdk/overlay';
import { ComponentPortal } from '@angular/cdk/portal';
import { Subject, BehaviorSubject } from 'rxjs';
import { generateUUID } from '@tc/foundation/utilities';
function StlrBreadComponent_Conditional_3_Template(rf, ctx) {
  if (rf & 1) {
    i0.ɵɵelementStart(0, "span", 1);
    i0.ɵɵtext(1);
    i0.ɵɵelementEnd();
  }
  if (rf & 2) {
    const ctx_r0 = i0.ɵɵnextContext();
    i0.ɵɵadvance();
    i0.ɵɵtextInterpolate1(" ", ctx_r0.data.message, " ");
  }
}
function StlrBreadComponent_Conditional_4_1_ng_template_0_Template(rf, ctx) {}
function StlrBreadComponent_Conditional_4_1_Template(rf, ctx) {
  if (rf & 1) {
    i0.ɵɵtemplate(0, StlrBreadComponent_Conditional_4_1_ng_template_0_Template, 0, 0, "ng-template");
  }
}
function StlrBreadComponent_Conditional_4_Template(rf, ctx) {
  if (rf & 1) {
    i0.ɵɵelementStart(0, "span", 1);
    i0.ɵɵtemplate(1, StlrBreadComponent_Conditional_4_1_Template, 1, 0, null, 3);
    i0.ɵɵelementEnd();
  }
  if (rf & 2) {
    const ctx_r0 = i0.ɵɵnextContext();
    i0.ɵɵadvance();
    i0.ɵɵproperty("ngTemplateOutlet", ctx_r0.contentAsTemplateRef);
  }
}
function StlrLoafComponent_For_2_Template(rf, ctx) {
  if (rf & 1) {
    const _r1 = i0.ɵɵgetCurrentView();
    i0.ɵɵelementStart(0, "li")(1, "stlr-bread", 0);
    i0.ɵɵlistener("closed", function StlrLoafComponent_For_2_Template_stlr_bread_closed_1_listener($event) {
      i0.ɵɵrestoreView(_r1);
      const ctx_r1 = i0.ɵɵnextContext();
      return i0.ɵɵresetView(ctx_r1.closeToast($event));
    });
    i0.ɵɵelementEnd()();
  }
  if (rf & 2) {
    const toast_r3 = ctx.$implicit;
    i0.ɵɵadvance();
    i0.ɵɵproperty("toast", toast_r3);
  }
}
class StlrBreadComponent {
  constructor() {
    this._iconTranslator = inject(IconTranslationService);
    this.ariaLive = 'assertive';
    this.role = 'alert';
    this.ariaAtomic = 'true';
    this.closed = new EventEmitter();
    /**
     * @ignore Internal tracking of timeout for disposal
     * @private
     */
    this._timeoutId = 5000;
  }
  /**
   * Gets the appropriate icon token for the toast type
   */
  get icon() {
    return this._iconTranslator.getIconForColor(this.toast?.data.type);
  }
  /**
   * Gets the toast's type as an iconColor type.
   */
  get color() {
    return this.data.type;
  }
  /**
   * Exposes the injected data to the template
   */
  get data() {
    return this.toast?.data;
  }
  get isContentSimple() {
    return typeof this.data.message === 'string';
  }
  get contentAsTemplateRef() {
    return !this.isContentSimple ? this.data.message : null;
  }
  ngOnInit() {
    // Close the toast after the passed in number of milliseconds to displayFor
    this._timeoutId = window.setTimeout(() => this.close(), this.data.displayFor);
    this.className = `stlr-bread ${this.data.type}`;
  }
  ngOnDestroy() {
    clearTimeout(this._timeoutId);
  }
  /**
   * Closes the toast immediately
   */
  close() {
    this.closed.emit(this.toast);
  }
  static {
    this.ɵfac = function StlrBreadComponent_Factory(__ngFactoryType__) {
      return new (__ngFactoryType__ || StlrBreadComponent)();
    };
  }
  static {
    this.ɵcmp = /* @__PURE__ */i0.ɵɵdefineComponent({
      type: StlrBreadComponent,
      selectors: [["stlr-bread"]],
      hostVars: 5,
      hostBindings: function StlrBreadComponent_HostBindings(rf, ctx) {
        if (rf & 2) {
          i0.ɵɵattribute("aria-live", ctx.ariaLive)("role", ctx.role)("aria-atomic", ctx.ariaAtomic);
          i0.ɵɵclassMap(ctx.className);
        }
      },
      inputs: {
        toast: "toast"
      },
      outputs: {
        closed: "closed"
      },
      standalone: false,
      decls: 6,
      vars: 2,
      consts: [[1, "toast-icon"], [1, "bread__content"], ["stlrButton", "", "btnType", "icon", "color", "light", "icon", "close", "aria-label", "Close", 1, "bread__close", 3, "click"], [4, "ngTemplateOutlet"]],
      template: function StlrBreadComponent_Template(rf, ctx) {
        if (rf & 1) {
          i0.ɵɵelementStart(0, "div", 0)(1, "stlr-icon");
          i0.ɵɵtext(2);
          i0.ɵɵelementEnd()();
          i0.ɵɵconditionalCreate(3, StlrBreadComponent_Conditional_3_Template, 2, 1, "span", 1)(4, StlrBreadComponent_Conditional_4_Template, 2, 1, "span", 1);
          i0.ɵɵelementStart(5, "button", 2);
          i0.ɵɵlistener("click", function StlrBreadComponent_Template_button_click_5_listener() {
            return ctx.close();
          });
          i0.ɵɵelementEnd();
        }
        if (rf & 2) {
          i0.ɵɵadvance(2);
          i0.ɵɵtextInterpolate1(" ", ctx.icon, " ");
          i0.ɵɵadvance();
          i0.ɵɵconditional(ctx.isContentSimple ? 3 : 4);
        }
      },
      dependencies: [i1.NgTemplateOutlet, i2.StlrIconComponent, i3.StlrButtonComponent],
      styles: ["[_nghost-%COMP%]{display:flex;align-items:center;overflow:hidden;min-width:calc(100% - 1rem);border-radius:var(--stlr-border-radius);box-shadow:0 0 6px 2px #0000001c;box-sizing:border-box;color:#fff;font-size:14px;transform-origin:center;animation:_ngcontent-%COMP%_open-toast .4s cubic-bezier(.47,1.64,.21,.8);will-change:transform;margin:var(--space--8)}[_nghost-%COMP%]   .toast-icon[_ngcontent-%COMP%]{display:flex;justify-content:center;position:relative;padding:var(--space--16);align-items:center;z-index:1;min-height:100%;align-self:stretch}[_nghost-%COMP%]   .toast-icon[_ngcontent-%COMP%]   stlr-icon[_ngcontent-%COMP%]     span{color:#fff;font-variation-settings:\"FILL\" 0,\"wght\" 500,\"GRAD\" 0,\"opsz\" 48}.warn[_nghost-%COMP%]{background-color:var(--stlr-color__warn--extra-dark)}.warn[_nghost-%COMP%]   .toast-icon[_ngcontent-%COMP%]{background-color:var(--stlr-color__warn)}.success[_nghost-%COMP%]{background-color:var(--stlr-color__success--extra-dark)}.success[_nghost-%COMP%]   .toast-icon[_ngcontent-%COMP%]{background-color:var(--stlr-color__success)}.info[_nghost-%COMP%]{background-color:var(--stlr-color__info--extra-dark)}.info[_nghost-%COMP%]   .toast-icon[_ngcontent-%COMP%]{background-color:var(--stlr-color__info)}.error[_nghost-%COMP%]{background-color:var(--stlr-color__error--extra-dark)}.error[_nghost-%COMP%]   .toast-icon[_ngcontent-%COMP%]{background-color:var(--stlr-color__error)}@media(min-width:37.5em){[_nghost-%COMP%]{min-width:350px;margin-bottom:1.5rem;margin-right:1.5rem}}.bread__content[_ngcontent-%COMP%]{width:100%;color:#fff;font-size:14px;font-weight:600;padding-inline:var(--space--16);padding-block:var(--space--12)}.bread__content[_ngcontent-%COMP%]     a{color:#fff;padding:2px 0;border-bottom:solid 1px white}.bread__content[_ngcontent-%COMP%]     a:hover{color:#fff;border-bottom:solid 1px white}.bread__close[_ngcontent-%COMP%]{display:flex;margin-inline:var(--space--16);appearance:none;border:0;background:none;color:#fff;cursor:pointer}@keyframes _ngcontent-%COMP%_open-toast{0%{transform:scale(0);opacity:0}to{transform:scale(1);opacity:1}}"]
    });
  }
}
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(StlrBreadComponent, [{
    type: Component,
    args: [{
      selector: 'stlr-bread',
      standalone: false,
      template: "<div class=\"toast-icon\">\n  <stlr-icon>\n    {{ icon }}\n  </stlr-icon>\n</div>\n\n@if (isContentSimple) {\n  <span class=\"bread__content\">\n    {{ data.message }}\n  </span>\n} @else {\n  <span class=\"bread__content\">\n    <ng-template *ngTemplateOutlet=\"contentAsTemplateRef\"> </ng-template>\n  </span>\n}\n\n\n<button\n  class=\"bread__close\"\n  stlrButton\n  btnType=\"icon\"\n  color=\"light\"\n  icon=\"close\"\n  (click)=\"close()\"\n  aria-label=\"Close\"\n></button>\n",
      styles: [":host{display:flex;align-items:center;overflow:hidden;min-width:calc(100% - 1rem);border-radius:var(--stlr-border-radius);box-shadow:0 0 6px 2px #0000001c;box-sizing:border-box;color:#fff;font-size:14px;transform-origin:center;animation:open-toast .4s cubic-bezier(.47,1.64,.21,.8);will-change:transform;margin:var(--space--8)}:host .toast-icon{display:flex;justify-content:center;position:relative;padding:var(--space--16);align-items:center;z-index:1;min-height:100%;align-self:stretch}:host .toast-icon stlr-icon ::ng-deep span{color:#fff;font-variation-settings:\"FILL\" 0,\"wght\" 500,\"GRAD\" 0,\"opsz\" 48}:host.warn{background-color:var(--stlr-color__warn--extra-dark)}:host.warn .toast-icon{background-color:var(--stlr-color__warn)}:host.success{background-color:var(--stlr-color__success--extra-dark)}:host.success .toast-icon{background-color:var(--stlr-color__success)}:host.info{background-color:var(--stlr-color__info--extra-dark)}:host.info .toast-icon{background-color:var(--stlr-color__info)}:host.error{background-color:var(--stlr-color__error--extra-dark)}:host.error .toast-icon{background-color:var(--stlr-color__error)}@media(min-width:37.5em){:host{min-width:350px;margin-bottom:1.5rem;margin-right:1.5rem}}.bread__content{width:100%;color:#fff;font-size:14px;font-weight:600;padding-inline:var(--space--16);padding-block:var(--space--12)}.bread__content ::ng-deep a{color:#fff;padding:2px 0;border-bottom:solid 1px white}.bread__content ::ng-deep a:hover{color:#fff;border-bottom:solid 1px white}.bread__close{display:flex;margin-inline:var(--space--16);appearance:none;border:0;background:none;color:#fff;cursor:pointer}@keyframes open-toast{0%{transform:scale(0);opacity:0}to{transform:scale(1);opacity:1}}\n"]
    }]
  }], null, {
    className: [{
      type: HostBinding,
      args: ['class']
    }],
    ariaLive: [{
      type: HostBinding,
      args: ['attr.aria-live']
    }],
    role: [{
      type: HostBinding,
      args: ['attr.role']
    }],
    ariaAtomic: [{
      type: HostBinding,
      args: ['attr.aria-atomic']
    }],
    toast: [{
      type: Input
    }],
    closed: [{
      type: Output
    }]
  });
})();

/**
 * Injection token for providing a default configuration
 */
const STLR_TOAST_CONFIG_TOKEN = new InjectionToken('STLR_TOAST_CONFIG_TOKEN');

/**
 * Data passed to every toast to tell it what to show and how
 */
class StlrToastData {
  constructor() {
    /**
     * The message to display
     */
    this.message = '';
    /**
     * The type of toast to render
     */
    this.type = 'info';
  }
}

/**
 * A container reference to the overlay holding the Toast
 */
class StlrToastRef {
  constructor() {
    this.id = `stlr-toast-${generateUUID()}`;
    this._closedSubject = new Subject();
  }
  /**
   * An observable the toast's opener can subscribe to and react to the closing of the toast.
   * This is wrapped in a function to be consistent with how the dialog service does this
   */
  afterClosed() {
    return this._closedSubject.asObservable();
  }
  /**
   * Closes the overlay and takes the toast with it
   */
  close() {
    if (this.onClose) {
      this.onClose(this.id);
    }
    this._closedSubject.next();
  }
}
class StlrToasterService {
  constructor() {
    this._overlay = inject(Overlay);
    this._parentInjector = inject(Injector);
    this._config = inject(STLR_TOAST_CONFIG_TOKEN, {
      optional: true
    });
    this._toasts = new BehaviorSubject([]);
    this.toasts$ = this._toasts.asObservable();
  }
  close(toastRef) {
    const toasts = this._toasts.value;
    const index = toasts.findIndex(ref => ref.id === toastRef.id);
    if (index > -1) {
      toasts.splice(index, 1);
    }
    this._toasts.next(toasts);
    toastRef.close();
  }
  /**
   * Shows a toast with 'error' styling
   * @param message The message to display
   * @param config Optional configuration settings for this toast instance
   */
  error(message, config) {
    this.show(message, 'error', config);
  }
  /**
   * Shows a toast with 'info' styling
   * @param message The message to display
   * @param config Optional configuration settings for this toast instance
   */
  info(message, config) {
    this.show(message, 'info', config);
  }
  /**
   * Shows a toast with 'success' styling
   * @param message The message to display
   * @param config Optional configuration settings for this toast instance
   */
  success(message, config) {
    this.show(message, 'success', config);
  }
  /**
   * Shows a toast with 'warn' styling
   * @param message The message to display
   * @param config (Optional) configuration settings for this toast instance
   */
  warn(message, config) {
    this.show(message, 'warn', config);
  }
  /**
   * Shows a toast
   * @param message The message to display
   * @param type The type of toast to render
   * @param config (Optional) configuration settings for this toast instance
   */
  show(message, type, config) {
    if (!config) {
      config = new StlrToastData();
    }
    // Fallback setting if it's not set anywhere else
    if (!config.displayFor) {
      config.displayFor = 5000;
    }
    config.type = type;
    config.message = message;
    this._show(config);
  }
  /**
   * Internal method for actually creating and showing toasts
   * @param data
   * @private
   */
  _show(data) {
    // Get our configuration models merged
    this._config = {
      ...(this._config || {
        position: {
          bottom: 0,
          right: 0
        }
      })
    };
    // Set up our overlay
    const positionStrategy = this._getPositionStrategy();
    if (!this._overlayRef) {
      this._overlayRef = this._overlay.create({
        positionStrategy,
        panelClass: 'stlr-toast-overlay'
      });
    }
    // Track this guy for later
    const toastRef = new StlrToastRef();
    toastRef.data = data;
    this._toasts.next([...this._toasts.value, toastRef]);
    if (!this._overlayRef?.hasAttached()) {
      // Inject the toast data and a reference to itself to the toast
      const injector = Injector.create({
        parent: this._parentInjector,
        providers: [{
          provide: StlrToastRef,
          useValue: toastRef
        }]
      });
      // Create a portal for the Loaf
      const loafPortal = new ComponentPortal(StlrLoafComponent, null, injector);
      // Attach the loaf to the UI for rendering
      this._overlayRef.attach(loafPortal);
    }
  }
  _getPositionStrategy() {
    if (this._config && this._config.position) {
      return this._overlay.position().global().bottom(`${this._config.position.bottom}px`).right(`${this._config.position.right}px`);
    }
    return this._overlay.position().global().bottom('0').right('0');
  }
  static {
    this.ɵfac = function StlrToasterService_Factory(__ngFactoryType__) {
      return new (__ngFactoryType__ || StlrToasterService)();
    };
  }
  static {
    this.ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
      token: StlrToasterService,
      factory: StlrToasterService.ɵfac
    });
  }
}
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(StlrToasterService, [{
    type: Injectable
  }], null, null);
})();
class StlrLoafComponent {
  constructor() {
    this.loafClass = true;
    this._toastService = inject(StlrToasterService);
    this.toasts$ = this._toastService.toasts$;
  }
  closeToast($event) {
    this._toastService.close($event);
  }
  static {
    this.ɵfac = function StlrLoafComponent_Factory(__ngFactoryType__) {
      return new (__ngFactoryType__ || StlrLoafComponent)();
    };
  }
  static {
    this.ɵcmp = /* @__PURE__ */i0.ɵɵdefineComponent({
      type: StlrLoafComponent,
      selectors: [["stlr-loaf"]],
      hostVars: 2,
      hostBindings: function StlrLoafComponent_HostBindings(rf, ctx) {
        if (rf & 2) {
          i0.ɵɵclassProp("stlr-loaf", ctx.loafClass);
        }
      },
      standalone: false,
      decls: 4,
      vars: 2,
      consts: [[3, "closed", "toast"]],
      template: function StlrLoafComponent_Template(rf, ctx) {
        if (rf & 1) {
          i0.ɵɵelementStart(0, "ol");
          i0.ɵɵrepeaterCreate(1, StlrLoafComponent_For_2_Template, 2, 1, "li", null, i0.ɵɵrepeaterTrackByIdentity);
          i0.ɵɵpipe(3, "async");
          i0.ɵɵelementEnd();
        }
        if (rf & 2) {
          i0.ɵɵadvance();
          i0.ɵɵrepeater(i0.ɵɵpipeBind1(3, 0, ctx.toasts$));
        }
      },
      dependencies: [StlrBreadComponent, i1.AsyncPipe],
      styles: ["ol[_ngcontent-%COMP%]{list-style:none;padding:0 1rem .3rem}@media(max-width:37.5em){  .stlr-toast-overlay{width:100%}  .stlr-toast-overlay .stlr-loaf{width:100%}  .stlr-toast-overlay .stlr-loaf ol{padding:0;margin:0}  .stlr-toast-overlay .stlr-loaf ol .stlr-bread{border-radius:0}}"],
      changeDetection: 0
    });
  }
}
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(StlrLoafComponent, [{
    type: Component,
    args: [{
      selector: 'stlr-loaf',
      changeDetection: ChangeDetectionStrategy.OnPush,
      standalone: false,
      template: "<ol>\n  @for (toast of toasts$ | async; track toast) {\n    <li>\n      <stlr-bread [toast]=\"toast\" (closed)=\"closeToast($event)\"></stlr-bread>\n    </li>\n  }\n</ol>\n",
      styles: ["ol{list-style:none;padding:0 1rem .3rem}@media(max-width:37.5em){::ng-deep .stlr-toast-overlay{width:100%}::ng-deep .stlr-toast-overlay .stlr-loaf{width:100%}::ng-deep .stlr-toast-overlay .stlr-loaf ol{padding:0;margin:0}::ng-deep .stlr-toast-overlay .stlr-loaf ol .stlr-bread{border-radius:0}}\n"]
    }]
  }], null, {
    loafClass: [{
      type: HostBinding,
      args: ['class.stlr-loaf']
    }]
  });
})();
class StlrToasterModule {
  static {
    this.ɵfac = function StlrToasterModule_Factory(__ngFactoryType__) {
      return new (__ngFactoryType__ || StlrToasterModule)();
    };
  }
  static {
    this.ɵmod = /* @__PURE__ */i0.ɵɵdefineNgModule({
      type: StlrToasterModule,
      declarations: [StlrBreadComponent, StlrLoafComponent],
      imports: [CommonModule, StlrIconModule, StlrButtonModule]
    });
  }
  static {
    this.ɵinj = /* @__PURE__ */i0.ɵɵdefineInjector({
      providers: [StlrToasterService],
      imports: [CommonModule, StlrIconModule, StlrButtonModule]
    });
  }
}
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(StlrToasterModule, [{
    type: NgModule,
    args: [{
      imports: [CommonModule, StlrIconModule, StlrButtonModule],
      declarations: [StlrBreadComponent, StlrLoafComponent],
      providers: [StlrToasterService]
    }]
  }], null, null);
})();

/**
 * Generated bundle index. Do not edit.
 */

export { STLR_TOAST_CONFIG_TOKEN, StlrBreadComponent, StlrLoafComponent, StlrToastData, StlrToastRef, StlrToasterModule, StlrToasterService };
